/**
 * Forecast overlay for the horizon slider — POLYGON.
 *
 * Source of truth is the product's stored predictions
 * (well_concentration_predictions), not a recomputed trend. This appends each
 * well/analyte's predicted concentration (at the chosen horizon) onto the
 * observed series, so every time-series chart extends to the prediction and the
 * Sankey's latest value becomes the forecast. Wells/analytes without a stored
 * prediction are left as observed-only.
 */
import type {
  AnalyticalTimeseriesResponse,
  AnalyticalTimeseriesAnalyte,
  PolygonWellPredictionRow,
} from '@/api/dataVault';

export function applyPolygonPredictions(
  data: AnalyticalTimeseriesResponse,
  predictions: PolygonWellPredictionRow[],
  horizonYears: number,
): AnalyticalTimeseriesResponse {
  if (horizonYears <= 0 || !predictions.length) return data;

  const byKey = new Map<string, PolygonWellPredictionRow>();
  for (const p of predictions) byKey.set(`${p.well_id}::${p.analyte_code}`, p);

  // Fallback date if the prediction row didn't carry a target_date.
  const fallbackDate = `${2024 + horizonYears}-01-01`;

  return {
    wells: data.wells.map((w) => {
      const analytes: Record<string, AnalyticalTimeseriesAnalyte> = { ...w.analytes };
      for (const [code, a] of Object.entries(w.analytes)) {
        const pred = byKey.get(`${w.well_id}::${code}`);
        if (pred && pred.predicted_conc_mgl != null) {
          analytes[code] = {
            ...a,
            measurements: [
              ...a.measurements,
              {
                date: pred.target_date ?? fallbackDate,
                concentration: pred.predicted_conc_mgl,
                concentration_ugl: pred.predicted_conc_mgl * 1000,
                is_non_detect: false,
                detection_limit: null,
              },
            ],
          };
        }
      }
      return { ...w, analytes };
    }),
  };
}
